import { styled } from '@mui/system';

import colors from '../../../config/theme/colors';
import { useIntl } from 'react-intl';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { StyledHeader } from '.';
import { IExtendedSearchField } from '../SearchAndCreation/ExtendedSearch/components/ExtendedSearchField';
import React from 'react';

const StyledTitle = styled('div')({
  color: colors.brandBlue.default,
  fontWeight: 600,
  fontSize: '1.6rem',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

/**
 * Platform selection list component
 * Provide either onChange or onChangePlatform
 * @param platforms - Available platform
 * @param onChange - onChange function (StatisticsTab)
 * @param onChangePlatform - onChangePlatform function (ExtendedSearch)
 * @returns {React.JSX.Element} - Platform selection list component
 */
export const PlatformSelectionList = ({
  platforms,
  onChange,
  onChangePlatform,
}: IPlatformSelectionList): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <>
      <StyledHeader>{fm('extendedSearch.diseaseOrTreatmentPlaceholder')}</StyledHeader>
      {platforms.map((platform, index) => (
        <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', width: '1.5rem', paddingTop: '0.2rem' }}>
            <svg
              transform="rotate(-90)"
              width="10"
              height="6"
              viewBox="0 0 10 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M5 5.71411L0.669872 0.267852L9.33013 0.267851L5 5.71411Z" fill={colors.brandBlue.default} />
            </svg>
          </div>
          <div style={{ display: 'flex', width: '100%' }}>
            <StyledTitle
              onClick={() =>
                onChange ? onChange(platform) : onChangePlatform ? onChangePlatform({ platform: platform }) : ''
              }
            >
              {fm(`header.platforms.${platform}`)}
            </StyledTitle>
          </div>
        </div>
      ))}
    </>
  );
};

interface IPlatformSelectionList {
  platforms: Platform[];
  onChange?: (platform: Platform) => void;
  onChangePlatform?: IExtendedSearchField['onChange'];
}

export const PlatformSelectionDropdown = ({
  platforms,
  selectedPlatform,
  onChangePlatform,
}: IPlatformSelectionDropdown) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <Container style={{ borderBottom: `1px solid ${colors.gray}`, paddingBottom: '2rem', alignItems: 'center' }}>
      <Item xs={7} md={8} style={{ fontWeight: 600, color: colors.primary, fontSize: '1.8rem' }}>
        {fm('extendedSearch.diseaseOrTreatment')}
      </Item>
      <Item xs={5} md={4}>
        {platforms.length === 1 ? (
          fm(`header.platforms.${platforms[0]}`)
        ) : platforms.length > 1 ? (
          <InputHandler
            type="Select"
            editing={true}
            name="platform"
            formData={{
              onChange: onChangePlatform,
              document: { platform: selectedPlatform },
            }}
            options={platforms}
            optionFormatter={(name: string | number): string => fm(`header.platforms.${name}`)}
            placeholder="extendedSearch.diseaseOrTreatmentPlaceholder"
          />
        ) : null}
      </Item>
    </Container>
  );
};

interface IPlatformSelectionDropdown {
  platforms: Platform[];
  selectedPlatform?: Platform;
  onChangePlatform?: IExtendedSearchField['onChange'];
}
